import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { usePlanUsage } from "@/hooks/usePlanUsage";
import { CAP_HIT_EVENT, type CapHitDetail } from "@/lib/cap-events";
import { trackEvent } from "@/lib/posthog";

/**
 * App-wide listener that opens a single CapHitModal when any denied action
 * dispatches CAP_HIT_EVENT (HTTP 429 / quota_exceeded, see lib/cap-events.ts).
 * Mounted once inside BrowserRouter (so the Upgrade CTA can navigate) and inside
 * AuthProvider (so usePlanUsage resolves the tenant's next tier). Debounced:
 * while the modal is open, re-dispatches are ignored.
 */
export function CapHitModalProvider() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<CapHitDetail | null>(null);

  useEffect(() => {
    const onCap = (e: Event) => {
      setDetail((e as CustomEvent<CapHitDetail>).detail ?? null);
      setOpen((cur) => (cur ? cur : true));
    };
    window.addEventListener(CAP_HIT_EVENT, onCap);
    return () => window.removeEventListener(CAP_HIT_EVENT, onCap);
  }, []);

  return <CapHitModal open={open} detail={detail} onClose={() => setOpen(false)} />;
}

function CapHitModal({
  open,
  detail,
  onClose,
}: {
  open: boolean;
  detail: CapHitDetail | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const u = usePlanUsage(companyId);
  const next = u.nextTier;
  const metric = u.metricLabel || "usage";

  // Prefer the exact numbers from the 429 body; fall back to the live hook.
  const current = detail?.current ?? (u.limit > 0 ? u.used : null);
  const limit = detail?.limit ?? (u.limit > 0 ? u.limit : null);

  const goUpgrade = () => {
    trackEvent("upgrade_cta_clicked", { source: "cap_hit_modal", next_tier: next?.id });
    onClose();
    navigate(next ? `/dash/settings/billing?upgrade=${next.id}` : "/dash/settings/billing");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 w-fit rounded-xl bg-warning/10 p-3">
            <Lock className="h-6 w-6 text-warning" />
          </div>
          <DialogTitle className="text-center">You've reached your monthly limit</DialogTitle>
          <DialogDescription className="text-center">
            {current != null && limit != null
              ? `You've used ${current.toLocaleString()} of ${limit.toLocaleString()} ${metric} this month`
              : `You've reached your monthly ${metric} limit`}
            {u.resetNote ? `. ${u.resetNote}` : "."}
          </DialogDescription>
        </DialogHeader>

        {next && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-center">
            <p className="text-sm text-muted-foreground">Upgrade to</p>
            <p className="text-lg font-bold text-foreground">
              {next.name}
              {typeof next.monthly === "number" && next.monthly > 0 ? ` · $${next.monthly}/mo` : ""}
            </p>
            <p className="text-xs text-muted-foreground">More {metric} every month, effective immediately.</p>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={goUpgrade} className="w-full">
            {next ? (
              <>
                Upgrade to {next.name} <ArrowUpRight className="ms-2 h-4 w-4" />
              </>
            ) : (
              "View plans"
            )}
          </Button>
          <Button variant="ghost" onClick={onClose} className="w-full">
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
